import {Button, Dialog, DialogContent, Paper} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import PopupRemove from 'components/Popups/PopupRemove';
import {i18n2} from 'features/i18n/i18n';
import {Form, Formik} from 'formik';
import React, {useEffect, useState} from 'react';
import Title from 'components/forms/Title';
import useAddParameter from '../../hook/useAddParameter';
import {useLoadParameter} from '../../hook/useLoadParameter';
import {parameterSchema} from '../../model/parameterSchema';
import {
  Domain,
  EmailGroup,
  FilesRoutes,
  Groups,
  LdapGroup,
  EmailContent,
  ExpirationDaysCapaInicial,
} from '../index';
import {post} from 'components/api/';
import {useGetToken} from 'components/hooks';
import {Loading} from 'components/grid-dx/loading/loading';
import PasswordPolicy from '../passwordPolicy/PasswordPolicy';
import IntegritySettingsGrid from '../IntegritySettingsGrid';

// 0 = no hubo cambio, 1 = cambio en progreso, 2 = cambio finalizado, 3 = editando de nuevo pero ya tuvo cambio
const passwordTypes = {
  notChanges: 0,
  inProgress: 1,
  finished: 2,
  editingAgain: 3,
};

const initialValues = {
  validateEmail: false,
  emailFrom: '',
  emailURL: '',
  host: '',
  port: 0,
  smtpUser: '',
  smtpPassword: '',
  useSsl: false,
  useStartTls: false,
  anonimo: true,
  validateDomain: false,
  domain: '',
  validateGroup: false,
  hseGroup: '',
  qaGroup: '',
  itpsGroup: '',
  collaboratorGroup: '',
  trainerGroup: '',
  userLDAP: '',
  password: '',
  serverLDAP: '',
  filesContentRoute: '',
  filesSRegistryRoute: '',
  pwdRequireMinLength: true,
  pwdMinLength: 10,
  pwdRequireNumber: true,
  pwdRequireSymbol: true,
  pwdDisallowObviousSequences: true,
  pwdDisallowUserData: true,
  enablePasswordHistory: true,
  passwordHistoryCount: 5,
  tokenExpirationMinutes: 60 * 24,
  idleTimeoutMinutes: 30,
  enableFailedLoginLockout: false,
  failedLoginAttemptsLimit: 5,
};

const Parameter = () => {
  const initial: any[] = [];

  const {data, loading: loadingParameter} = useLoadParameter();
  const addParameter = useAddParameter();
  const [open, setOpen] = useState(false);
  const [parameter, setParameter] = useState<any>({...initialValues});
  const [redirect, setRedirect] = useState(false);
  const token = useGetToken();
  const [loading, setLoading] = useState(false);
  const [passwordChangeFinished, setPasswordChangeFinished] = useState(
    passwordTypes.notChanges,
  );
  const [settingsId, setSettingsId] = useState<number | null>(null);
  const [openHistory, setOpenHistory] = useState(false);
  const [onlyTwoParamsOptions, setOnlyTwoParamsOptions] = useState({
    AddedOne: parameter,
    AddedTwo: initial,
  });

  const changeInstances = () => {
    setLoading(true);
    post('api/Stage/ChangeExpirationDate', token).then((response) => {
      setLoading(false);
    });
  };

  const submit = (values: any, formikBag: any) => {
    // If anonimo is selected, ensure no SMTP credentials are sent
    const sanitized = {...values};
    if (sanitized.anonimo) {
      sanitized.smtpUser = null;
      sanitized.smtpPassword = null;
    }

    let addedOne = {
      ...sanitized,
      passwordChanged:
        passwordChangeFinished === passwordTypes.finished ? true : false,
    };
    setOnlyTwoParamsOptions({AddedOne: addedOne, AddedTwo: initial});
    formikBag && formikBag.setSubmitting(false);
    setOpen(true);
  };

  useEffect(() => {
    if (!data) return;

    const {
      user: lowerUser,
      User: upperUser,
      id_Settings,
      ID_Settings,
      id_settings,
      ID_settings,
      id,
      ID,
      ...rest
    } = data;

    const userLDAP = lowerUser ?? upperUser;

    // determine anonimo based on presence of smtpUser
    const smtpUser = data.smtpUser || (data as any).SmtpUser || '';
    const anonimo = !smtpUser;

    const rawId =
      id_Settings ??
      ID_Settings ??
      (data as any).Id_Settings ??
      (data as any).iD_Settings ??
      id_settings ??
      ID_settings ??
      id ??
      ID;
    let parsedId: number | null = null;
    if (typeof rawId === 'string') {
      const numericId = parseInt(rawId, 10);
      parsedId = Number.isNaN(numericId) ? null : numericId;
    } else if (typeof rawId === 'number') {
      parsedId = rawId;
    }

    setSettingsId(parsedId);

    const tokenExpirationMinutes =
      (data as any).tokenExpirationMinutes ??
      (data as any).TokenExpirationMinutes ??
      initialValues.tokenExpirationMinutes;

    const idleTimeoutMinutes =
      (data as any).idleTimeoutMinutes ??
      (data as any).IdleTimeoutMinutes ??
      initialValues.idleTimeoutMinutes;

    const enableFailedLoginLockout =
      (data as any).enableFailedLoginLockout ??
      (data as any).EnableFailedLoginLockout ??
      initialValues.enableFailedLoginLockout;

    const failedLoginAttemptsLimit =
      (data as any).failedLoginAttemptsLimit ??
      (data as any).FailedLoginAttemptsLimit ??
      initialValues.failedLoginAttemptsLimit;

    setParameter({
      userLDAP,
      ...rest,
      anonimo,
      tokenExpirationMinutes,
      idleTimeoutMinutes,
      enableFailedLoginLockout,
      failedLoginAttemptsLimit,
    });
  }, [data]);

  return (
    <Formik
      initialValues={{...parameter}}
      onSubmit={submit}
      enableReinitialize
      validationSchema={parameterSchema}>
      {(formikProps) => {
        return (
          <Paper style={{padding: '10px', lineHeight: 0}}>
            <Form>
              <h4>{i18n2._('Política de contraseñas')}</h4>
              <PasswordPolicy formikProps={formikProps} />
              <h4>{i18n2._('Notificaciones via email')}</h4>
              <EmailGroup formikProps={formikProps} />
              <h4>{i18n2._('URL en el email')}</h4>
              <EmailContent formikProps={formikProps} />
              <h4>{i18n2._('Dominio')}</h4>
              <Domain formikProps={formikProps} />
              <h4>{i18n2._('Grupo')}</h4>
              <Groups formikProps={formikProps} />

              <h4>
                {i18n2._('Tiempo de expiración de capacitaciones (en días)')}
              </h4>
              <ExpirationDaysCapaInicial />
              <h4>{i18n2._('LDAP')}</h4>
              <LdapGroup
                formikProps={formikProps}
                passwordChangeFinished={passwordChangeFinished}
                passwordTypes={passwordTypes}
                setPasswordChangeFinished={setPasswordChangeFinished}
              />
              <h4>{i18n2._('Rutas')}</h4>
              <FilesRoutes />
              {open && (
                <PopupRemove
                  open={open}
                  promiseToReturn={addParameter}
                  setOpen={() => setOpen(false)}
                  dialogTitle={i18n2._('Confirmar operación')}
                  types={onlyTwoParamsOptions}
                  url={'/Dashboard'}
                  redirect={redirect}
                  setRedirect={setRedirect}
                />
              )}
              <Grid
                item
                xs={12}
                container
                alignItems="center"
                justify="space-between"
                style={{marginTop: '10px'}}>
                <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={changeInstances}
                    disabled={loading}>
                    {i18n2._('Cambiar fecha de expiración')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setOpenHistory(true)}
                    disabled={
                      settingsId === null || loading || loadingParameter
                    }>
                    {i18n2._('Ver historial')}
                  </Button>
                </div>
                <Button
                  type="submit"
                  disabled={
                    (passwordChangeFinished === passwordTypes.notChanges ||
                      passwordChangeFinished === passwordTypes.finished) &&
                    !loading
                      ? false
                      : true
                  }
                  variant="contained"
                  color="primary">
                  {i18n2._('Guardar')}
                </Button>
              </Grid>
            </Form>
            <Dialog
              open={openHistory}
              onClose={() => setOpenHistory(false)}
              fullWidth
              maxWidth="xl"
              PaperProps={{
                style: {
                  margin: '24px',
                  width: 'calc(100% - 48px)',
                  maxWidth: '1400px',
                  overflowX: 'auto',
                },
              }}>
              <Title
                onClose={() => setOpenHistory(false)}
                title={i18n2._('Historial de parámetros generales')}
              />
              <DialogContent>
                {settingsId ? (
                  <IntegritySettingsGrid settingsId={settingsId} />
                ) : (
                  <div style={{padding: '12px'}}>
                    {i18n2._(
                      'No se encontró un identificador de configuración',
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
            {(loading || loadingParameter) && <Loading />}
          </Paper>
        );
      }}
    </Formik>
  );
};

export default Parameter;
